import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList,
  faUserClock,
  faScrewdriverWrench,
  faClipboardCheck,
  faRotateLeft,
  faBriefcase,
  faHandshake,
  faCalendarCheck,
  faUserPlus,
  faUserGroup,
  faListCheck,
  faComments,
  faFileLines,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '@/stores/auth.store';
import { useHasPermission } from '@/hooks/use-permissions';
import { dashboardService } from '@/services/dashboard.service';
import { KpiCard, KpiCardSkeleton } from './components/KpiCard';
import {
  OperationsReviewsTable,
  OperationsReviewsTableSkeleton,
} from './components/OperationsReviewsTable';
import { ReportReviewsTable, ReportReviewsTableSkeleton } from './components/ReportReviewsTable';
import { LifecycleStageChart, LifecycleStageChartSkeleton } from './components/LifecycleStageChart';
import { RecentActivityFeed, RecentActivitySkeleton } from './components/RecentActivityFeed';

const QUICK_ACTION_ICON: Record<string, IconDefinition> = {
  faClipboardList,
  faUserPlus,
  faClipboardCheck,
  faUserGroup,
  faListCheck,
};

export function OperationsDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const can = useHasPermission();
  const navigate = useNavigate();
  const firstName = user?.firstName ?? 'there';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard-operations'],
    queryFn: dashboardService.getOperationsDashboard,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Welcome back, {firstName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Operations dashboard — intake, assignment, and review at a glance.
        </p>
      </div>

      {isError && (
        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load dashboard. Check your connection and refresh.
        </div>
      )}

      {/* ── Top: KPI cards ──────────────────────────────────────────────── */}
      <section>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            Array.from({ length: 10 }).map((_, i) => <KpiCardSkeleton key={i} />)
          ) : data ? (
            <>
              {/* Request Thread Stage B — the Operations discovery entry point.
                  Links to the work queue PRE-FILTERED to the one state that
                  means "Operations must act" (OPS_REVIEW); the queue's own
                  global default stays unfiltered (OD-B2). */}
              <KpiCard
                label="Modification Requests Awaiting Operations"
                value={data.kpis.modificationRequestsAwaitingOperations}
                icon={faComments}
                iconCls="bg-rose-500/10 text-rose-700"
                to="/operations/work-queue?modificationRoundState=OPS_REVIEW"
              />
              <KpiCard
                label="Service Reports Awaiting Review"
                value={data.kpis.pendingReportReviews}
                icon={faFileLines}
                iconCls="bg-amber-500/10 text-amber-700"
                to="/operations/work-queue"
              />
              <KpiCard
                label="Today's Inward"
                value={data.kpis.todaysIntakeCount}
                icon={faClipboardList}
                iconCls="bg-blue-500/10 text-blue-700"
                to="/equipment-intake"
              />
              <KpiCard
                label="Waiting for Engineer Assignment"
                value={data.kpis.waitingForEngineerAssignment}
                icon={faUserClock}
                iconCls="bg-slate-500/10 text-slate-700"
                to="/operations/assignment-center"
              />
              <KpiCard
                label="Inspection In Progress"
                value={data.kpis.inspectionInProgress}
                icon={faScrewdriverWrench}
                iconCls="bg-amber-500/10 text-amber-700"
                to="/operations/work-queue?status=IN_PROGRESS"
              />
              <KpiCard
                label="Pending OPS Review"
                value={data.kpis.pendingOpsReviews}
                icon={faClipboardCheck}
                iconCls="bg-orange-500/10 text-orange-700"
                to="/operations/work-queue?status=COMPLETED&operationsReviewStatus=PENDING"
              />
              <KpiCard
                label="Returned to Engineer"
                value={data.kpis.returnedToEngineer}
                icon={faRotateLeft}
                iconCls="bg-red-500/10 text-red-700"
                to="/operations/work-queue?operationsReviewStatus=RETURNED"
              />
              <KpiCard
                label="Waiting for Commercial"
                value={data.kpis.waitingForCommercial}
                icon={faBriefcase}
                iconCls="bg-green-500/10 text-green-700"
                to="/operations/work-queue?operationsReviewStatus=APPROVED_FOR_COMMERCIAL"
              />
              <KpiCard
                label="Waiting for Customer Approval"
                value={data.kpis.pendingCustomerDecisions}
                icon={faHandshake}
                iconCls="bg-purple-500/10 text-purple-700"
                to="/operations/work-queue?customerApprovalStatus=PENDING"
              />
              <KpiCard
                label="Completed Today"
                value={data.kpis.completedToday}
                icon={faCalendarCheck}
                iconCls="bg-teal-500/10 text-teal-700"
                to="/operations/work-queue?status=COMPLETED"
              />
            </>
          ) : null}
        </div>
      </section>

      {/* ── ACTION REQUIRED ──────────────────────────────────────────────────
          B6.17 — the two OPS review queues, kept visibly distinct (DEC-1).
          Service Report review leads because it is the workflow SE/MSE actually
          use (DEC-3); Inspection review is preserved beneath it, unchanged. */}
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <FontAwesomeIcon icon={faTriangleExclamation} className="h-3 w-3" />
            Action Required
          </h2>
          {isLoading ? (
            <>
              <ReportReviewsTableSkeleton />
              <OperationsReviewsTableSkeleton />
            </>
          ) : data ? (
            <>
              <ReportReviewsTable items={data.pendingReportReviews} />
              <OperationsReviewsTable items={data.pendingReviews} />
            </>
          ) : null}
        </div>

        <div className="space-y-6">
          {isLoading ? (
            <>
              <LifecycleStageChartSkeleton />
              <RecentActivitySkeleton />
            </>
          ) : data ? (
            <>
              <LifecycleStageChart data={data.jobsByLifecycleStage} />
              <RecentActivityFeed items={data.recentActivity} />
            </>
          ) : null}
        </div>
      </section>

      {/* ── Bottom: Quick Actions ───────────────────────────────────────── */}
      {data && data.quickActions.length > 0 && (
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {data.quickActions
              .filter((action) => !action.permission || can(action.permission))
              .map((action) => (
                <button
                  key={action.label}
                  onClick={() => navigate(action.route)}
                  className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 text-center transition-colors hover:bg-muted/30"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <FontAwesomeIcon
                      icon={QUICK_ACTION_ICON[action.icon] ?? faClipboardList}
                      className="h-4 w-4"
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground">{action.label}</span>
                </button>
              ))}
          </div>
        </section>
      )}
    </div>
  );
}
